// Textarea
// ---------
// Multi-line input. Same variants and error state as Input.
//
// Defaults to a minimum height of 80px so it doesn't collapse to a single
// line on first paint. Override via `rows` or `className` if you need more.

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const textareaVariants = cva(
  "flex w-full min-h-[80px] rounded-lg border px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 resize-y",
  {
    variants: {
      variant: {
        light:
          "border-gray-200 bg-white text-gray-900 placeholder:text-gray-400 focus:border-blue-500/40 focus:ring-blue-500/40",
        dark:
          "border-white/[0.08] bg-white/[0.04] text-white placeholder:text-white/25 focus:border-blue-500/40 focus:ring-blue-500/40",
      },
      hasError: {
        true: "border-red-500 focus:border-red-500 focus:ring-red-500/40",
        false: "",
      },
    },
    defaultVariants: {
      variant: "light",
      hasError: false,
    },
  }
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, hasError, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(textareaVariants({ variant, hasError }), className)}
        {...props}
      />
    );
  }
);
Textarea.displayName = "Textarea";

export { Textarea, textareaVariants };
